import { useMemo, useState } from "react";
import { addDays, addMonths, todayIn, type IsoDate } from "../../../shared/dates";
import { isDrivable } from "../../../shared/drive";
import type { CalendarEvent, CalendarFeed, Member } from "../../../shared/models";
import { eventKey } from "../../../shared/people";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useCalendarEvents, useMe, useMembers, useMoveEvent, useSetEventPeople } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { DirectionsButton } from "../../ui/Directions";
import { Icon } from "../../ui/Icon";
import { OutsideLink } from "../../ui/OutsideLink";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { formatShortTime, formatTime, useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { remPx, useMeasure } from "../sky/common";
import { WidgetHeader } from "../WidgetHeader";
import { daysBetween, eventsOnDay, fmtDate, monthGrid, placeEvent, shiftedEvent, shiftedTimes, weekStart, type PlacedEvent } from "./calendarModel";
import { dropClass, useEventDrag, type DragControl } from "./eventDrag";
import { DeleteEvent, EventForm } from "./EventForm";

type View = "today" | "week" | "month" | "agenda";
const VIEWS: { key: View; label: string }[] = [
  { key: "today", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "agenda", label: "Agenda" },
];

interface CalendarConfig {
  view: View;
  agendaDays: number;
}

/** Date range to fetch for a view. Day view also needs its mini-month. */
function rangeFor(view: View, anchor: IsoDate, agendaDays: number): { start: IsoDate; end: IsoDate } {
  if (view === "month" || view === "today") {
    const g = monthGrid(anchor);
    return { start: g.start, end: g.end };
  }
  if (view === "week") {
    const s = weekStart(anchor);
    return { start: s, end: addDays(s, 7) };
  }
  return { start: anchor, end: addDays(anchor, agendaDays) };
}

function CalendarBody({ config, size, editing, fullscreen, openFullscreen }: WidgetProps<CalendarConfig>) {
  const { timeZone, settings } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  // Month grids are unreadable on a phone; start narrow tiles in agenda view.
  const startView: View = size.width < 520 && config.view === "month" ? "agenda" : config.view;
  const [view, setView] = useState<View>(startView);
  // A new default (the layout was edited, or arrived after the cached copy) takes over.
  const [shownDefault, setShownDefault] = useState(config.view);
  if (shownDefault !== config.view) {
    setShownDefault(config.view);
    setView(startView);
  }
  const [anchor, setAnchor] = useState<IsoDate | null>(null);
  const current = anchor ?? today;
  const range = rangeFor(view, current, config.agendaDays);
  const q = useCalendarEvents(range.start, range.end);
  const state = dataStateOf(q, 20 * 60_000);
  const [openDay, setOpenDay] = useState<IsoDate | null>(null);
  const [openEvent, setOpenEvent] = useState<PlacedEvent | null>(null);
  // null = closed; "new" = add; otherwise the event being edited.
  const [form, setForm] = useState<CalendarEvent | "new" | null>(null);
  const [deleting, setDeleting] = useState<CalendarEvent | null>(null);
  const h24 = settings.clock24h;
  const canAdd = Boolean(q.data?.canEdit && q.data.calendars?.some((c) => c.canEdit));

  const members = useMembers().data ?? [];
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const [person, setPerson] = useState<string | null>(null);
  // Events take the color of the person they're for; untagged events keep the calendar color.
  const placed = useMemo(
    () =>
      (q.data?.events ?? [])
        .filter((e) => e.status !== "cancelled" && (!person || e.people.includes(person)))
        .map((e) => placeEvent({ ...e, color: (e.people[0] && byId.get(e.people[0])?.color) || e.color }, timeZone)),
    [q.data, timeZone, byId, person],
  );

  // Drag an event to another day: only real, editable events, and never while the screen is being arranged.
  const { toast } = useAppUi();
  const move = useMoveEvent();
  const canDrag = (p: PlacedEvent) => {
    const feed = q.data;
    return !editing && feed?.source === "google" && feed.canEdit && p.event.canEdit && Boolean(feed.calendars?.find((c) => c.id === p.event.calendarId)?.canEdit);
  };
  const moveEvent = (p: PlacedEvent, from: IsoDate, to: IsoDate) => {
    const days = daysBetween(from, to);
    // The event as served (placed events carry the person's color instead of their own).
    const event = q.data?.events.find((e) => e.id === p.event.id && e.calendarId === p.event.calendarId);
    if (!days || !event) return;
    const name = `“${event.displayTitle}”`;
    const when = fmtDate(addDays(p.firstDate, days), { weekday: "short", month: "short", day: "numeric" });
    move
      .mutateAsync({ event, moved: shiftedEvent(event, days, timeZone), input: shiftedTimes(event, days, timeZone) })
      .then((saved) =>
        toast(`Moved ${name} to ${when}`, {
          label: "Undo",
          run: () => {
            move.mutateAsync({ event: saved, moved: event, input: shiftedTimes(event, 0, timeZone) }).catch((e: Error) => toast(`Couldn’t undo moving ${name}: ${e.message}`));
          },
        }),
      )
      .catch((e: Error) => toast(`Couldn’t move ${name}: ${e.message}`));
  };
  const dnd = useEventDrag(canDrag, moveEvent);

  const step = (dir: 1 | -1) => {
    if (view === "month") setAnchor(addMonths(`${current.slice(0, 7)}-01`, dir));
    else if (view === "week") setAnchor(addDays(current, 7 * dir));
    else if (view === "today") setAnchor(addDays(current, dir));
    else setAnchor(addDays(current, config.agendaDays * dir));
  };

  const title =
    view === "today"
      ? current === today
        ? "Today"
        : current === addDays(today, 1)
          ? "Tomorrow"
          : fmtDate(current, { weekday: "long" })
      : view === "month"
        ? fmtDate(current, { month: "long", year: "numeric" })
        : view === "week"
          ? "This week"
          : "Coming up";
  const meta =
    view === "today"
      ? fmtDate(current, { weekday: "short", month: "short", day: "numeric" })
      : view === "week"
        ? `${fmtDate(range.start, { month: "short", day: "numeric" })} – ${fmtDate(addDays(range.end, -1), { month: "short", day: "numeric" })}`
        : undefined;

  return (
    <div className={`cal ${dnd.drag ? "cal-dragging" : ""}`}>
      <WidgetHeader
        title={title}
        meta={meta}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <>
            <button className="icon-btn" onClick={() => step(-1)} aria-label="Previous">
              <Icon name="chevronLeft" />
            </button>
            <button className="icon-btn" onClick={() => step(1)} aria-label="Next">
              <Icon name="chevronRight" />
            </button>
            {anchor && (
              <button className="btn btn-sm" onClick={() => setAnchor(null)}>
                Today
              </button>
            )}
            {canAdd && !editing && (
              <button className="icon-btn" onClick={() => setForm("new")} aria-label="Add event">
                <Icon name="plus" />
              </button>
            )}
            {size.variant !== "compact" && (
              <div className="segmented" role="tablist" aria-label="Calendar view">
                {VIEWS.map((v) => (
                  <button key={v.key} role="tab" aria-selected={view === v.key} className={view === v.key ? "on" : ""} onClick={() => setView(v.key)}>
                    {v.label}
                  </button>
                ))}
              </div>
            )}
          </>
        }
      />

      <SourceBanner feed={q.data} />
      {members.length > 1 && size.variant !== "compact" && (size.height > remPx() * 21.4 || fullscreen) && (
        <div className="person-filter" role="tablist" aria-label="Show events for">
          <button className={`chip chip-sm ${person === null ? "on" : ""}`} onClick={() => setPerson(null)}>
            Everyone
          </button>
          {members.map((m) => (
            <button key={m.id} className={`person-chip ${person === m.id ? "on" : ""}`} onClick={() => setPerson(person === m.id ? null : m.id)} aria-pressed={person === m.id}>
              <Avatar member={m} size="1.7rem" />
              <span>{m.displayName}</span>
            </button>
          ))}
        </div>
      )}

      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="calendar" title="Calendar unavailable" detail={q.error?.message} />
      ) : view === "today" ? (
        <DayView day={current} today={today} placed={placed} timeZone={timeZone} h24={h24} wide={size.width > 620} byId={byId} onPickDay={setAnchor} onEvent={setOpenEvent} dnd={dnd} />
      ) : view === "month" ? (
        <MonthView anchor={current} today={today} placed={placed} timeZone={timeZone} h24={h24} height={size.height} onDay={setOpenDay} onEvent={setOpenEvent} dnd={dnd} />
      ) : view === "week" ? (
        <WeekView start={range.start} today={today} placed={placed} timeZone={timeZone} h24={h24} onEvent={setOpenEvent} onDay={setOpenDay} dnd={dnd} />
      ) : (
        <AgendaView start={range.start} days={config.agendaDays} today={today} placed={placed} timeZone={timeZone} h24={h24} byId={byId} onEvent={setOpenEvent} />
      )}

      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {dnd.ghost}

      {openDay && !editing && (
        <Sheet title={fmtDate(openDay, { weekday: "long", month: "long", day: "numeric" })} onClose={() => setOpenDay(null)}>
          <DayList items={eventsOnDay(placed, openDay)} timeZone={timeZone} h24={h24} byId={byId} onEvent={(p) => setOpenEvent(p)} day={openDay} />
        </Sheet>
      )}
      {openEvent && !editing && (
        <EventDetail
          placed={openEvent}
          live={q.data?.events.find((e) => e.id === openEvent.event.id) ?? openEvent.event}
          members={members}
          timeZone={timeZone}
          h24={h24}
          source={q.data?.source ?? "none"}
          onClose={() => setOpenEvent(null)}
          onEdit={(ev) => {
            setOpenEvent(null);
            setForm(ev);
          }}
          onDelete={(ev) => setDeleting(ev)}
        />
      )}
      {form && !editing && (
        <EventForm
          event={form === "new" ? null : form}
          day={openDay ?? current}
          calendars={q.data?.calendars ?? []}
          members={members}
          timeZone={timeZone}
          onClose={() => setForm(null)}
        />
      )}
      {deleting && (
        <DeleteEvent
          event={deleting}
          onCancel={() => setDeleting(null)}
          onDone={() => {
            setDeleting(null);
            setOpenEvent(null);
          }}
        />
      )}
    </div>
  );
}

function SourceBanner({ feed }: { feed: CalendarFeed | undefined }) {
  const { openSettings } = useAppUi();
  const { isAdmin } = useHousehold();
  // The demo household has no Google account to connect.
  const demo = useMe().data?.demo === true;
  if (feed?.source === "google" && feed.note) {
    return (
      <div className="sample-banner" role="note">
        <Icon name={feed.stale ? "wifiOff" : "info"} size="1rem" />
        <span>{feed.note}</span>
      </div>
    );
  }
  if (!feed || feed.source !== "sample") return null;
  return (
    <div className="sample-banner" role="note">
      <Icon name="info" size="1rem" />
      <span>Sample events — not your calendar.</span>
      {isAdmin && !demo && (
        <button className="link-btn" onClick={() => openSettings("calendar")}>
          Connect Google Calendar
        </button>
      )}
    </div>
  );
}

const color = (p: PlacedEvent) => p.event.color ?? "var(--accent)";

function timeLabel(p: PlacedEvent, day: IsoDate, timeZone: string, h24: boolean, short = false): string {
  if (p.event.allDay) return "All day";
  if (p.firstDate !== day) return "Cont.";
  return short ? formatShortTime(new Date(p.startMs), timeZone, h24) : formatTime(new Date(p.startMs), timeZone, h24);
}

/** Day schedule beside a mini month with event dots — the wall's default calendar view. */
function DayView({
  day,
  today,
  placed,
  timeZone,
  h24,
  wide,
  byId,
  onPickDay,
  onEvent,
  dnd,
}: {
  day: IsoDate;
  today: IsoDate;
  placed: PlacedEvent[];
  timeZone: string;
  h24: boolean;
  wide: boolean;
  byId: Map<string, Member>;
  onPickDay: (d: IsoDate | null) => void;
  onEvent: (p: PlacedEvent) => void;
  dnd: DragControl;
}) {
  const now = Date.now();
  const items = eventsOnDay(placed, day);
  const nextIdx = day === today ? items.findIndex((p) => !p.event.allDay && p.endMs > now) : -1;
  const weekEnd = addDays(weekStart(today), 7);
  const countIn = (from: IsoDate, to: IsoDate) => {
    let n = 0;
    for (let d = from; d < to; d = addDays(d, 1)) n += eventsOnDay(placed, d).length;
    return n;
  };
  return (
    <div className={`cal-day ${wide ? "wide" : ""}`}>
      <ol className="schedule">
        {items.length === 0 && <li className="schedule-empty">Nothing scheduled{day === today ? " today" : ""}.</li>}
        {items.map((p, i) => {
          const past = day === today && !p.event.allDay && p.endMs <= now;
          return (
            <li key={p.event.id} className={`${past ? "past" : ""} ${i === nextIdx ? "next" : ""}`}>
              {/* Events drag onto the mini month beside the schedule. */}
              <button onClick={() => onEvent(p)} style={{ ["--ev" as string]: color(p) }} {...(wide ? dnd.bind(p, day) : {})}>
                <span className="sch-time">{timeLabel(p, day, timeZone, h24)}</span>
                <span className="sch-dot" />
                <span className="sch-title">{p.event.displayTitle}</span>
                {p.event.location && <span className="sch-sub">{p.event.location}</span>}
                <People ids={p.event.people} byId={byId} />
              </button>
            </li>
          );
        })}
      </ol>
      {wide && (
        <div className="mini-cal">
          <MiniMonth anchor={day} selected={day} today={today} placed={placed} onPick={(d) => onPickDay(d === today ? null : d)} dnd={dnd} />
          <div className="mini-counts">
            <button className="pill" onClick={() => onPickDay(null)}>
              Today <b>{countIn(today, addDays(today, 1))}</b>
            </button>
            <button className="pill" onClick={() => onPickDay(addDays(today, 1))}>
              Tomorrow <b>{countIn(addDays(today, 1), addDays(today, 2))}</b>
            </button>
            <span className="pill static">
              This week <b>{countIn(today, weekEnd)}</b>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniMonth({
  anchor,
  selected,
  today,
  placed,
  onPick,
  dnd,
}: {
  anchor: IsoDate;
  selected: IsoDate;
  today: IsoDate;
  placed: PlacedEvent[];
  onPick: (d: IsoDate) => void;
  dnd: DragControl;
}) {
  const g = monthGrid(anchor);
  const month = anchor.slice(0, 7);
  return (
    <div className="mini-month">
      <div className="mini-title">{fmtDate(anchor, { month: "long", year: "numeric" })}</div>
      <div className="mini-grid">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i} className="mini-dow">
            {d}
          </span>
        ))}
        {g.weeks.flat().map((d) => {
          const evs = eventsOnDay(placed, d).slice(0, 3);
          return (
            <button
              key={d}
              className={`mini-day ${d.slice(0, 7) !== month ? "other" : ""} ${d === today ? "today" : ""} ${d === selected ? "sel" : ""} ${dropClass(dnd, d)}`}
              data-drop-day={d}
              onClick={() => onPick(d)}
              aria-label={fmtDate(d, { weekday: "long", month: "long", day: "numeric" })}
            >
              <span>{Number(d.slice(8))}</span>
              <span className="mini-dots">
                {evs.map((p) => (
                  <i key={p.event.id} style={{ background: color(p) }} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ p, day, timeZone, h24, onEvent, dnd }: { p: PlacedEvent; day: IsoDate; timeZone: string; h24: boolean; onEvent: (p: PlacedEvent) => void; dnd: DragControl }) {
  return (
    <button
      className={`cal-chip ${p.event.allDay ? "all-day" : ""}`}
      style={{ ["--ev" as string]: color(p) }}
      {...dnd.bind(p, day)}
      onClick={(e) => {
        e.stopPropagation();
        onEvent(p);
      }}
    >
      {!p.event.allDay && <span className="cal-chip-time">{timeLabel(p, day, timeZone, h24, true)}</span>}
      <span className="cal-chip-title">{p.event.displayTitle}</span>
    </button>
  );
}

function MonthView({
  anchor,
  today,
  placed,
  timeZone,
  h24,
  height,
  onDay,
  onEvent,
  dnd,
}: {
  anchor: IsoDate;
  today: IsoDate;
  placed: PlacedEvent[];
  timeZone: string;
  h24: boolean;
  height: number;
  onDay: (d: IsoDate) => void;
  onEvent: (p: PlacedEvent) => void;
  dnd: DragControl;
}) {
  const grid = monthGrid(anchor);
  const month = anchor.slice(0, 7);
  const rowPx = (height - 130) / grid.weeks.length;
  const fontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  // Chips wrap to a second line rather than cut a title short, so measure each
  // chip's text against the cell width and show only the chips that fit.
  const [monthRef, box] = useMeasure<HTMLDivElement>();
  const textW = box.width ? (box.width - fontPx * 0.3 * 6) / 7 - fontPx * 1.4 - 4 : 0;
  const measure = useMemo(() => document.createElement("canvas").getContext("2d"), []);
  const family = getComputedStyle(document.body).fontFamily;
  const chipLines = (p: PlacedEvent, d: IsoDate) => {
    if (!measure || textW <= 0) return 1;
    let w = 0;
    if (!p.event.allDay) {
      measure.font = `600 ${fontPx * 0.8}px ${family}`;
      w += measure.measureText(timeLabel(p, d, timeZone, h24, true)).width + fontPx * 0.3;
    }
    measure.font = `${p.event.allDay ? 600 : 400} ${fontPx * 0.8}px ${family}`;
    w += measure.measureText(p.event.displayTitle).width;
    return w <= textW ? 1 : 2;
  };
  const fitCount = (items: PlacedEvent[], d: IsoDate) => {
    let room = rowPx - fontPx * 1.9;
    for (let i = 0; i < items.length; i++) {
      const h = (chipLines(items[i]!, d) === 1 ? 1.45 : 2.3) * fontPx + fontPx * 0.15;
      // Leave a line for "+N more" unless this is the last chip.
      if (h + (i < items.length - 1 ? fontPx * 1.1 : 0) > room) return i;
      room -= h;
    }
    return items.length;
  };
  return (
    <div className="cal-month" ref={monthRef} style={{ gridTemplateRows: `auto repeat(${grid.weeks.length}, 1fr)` }}>
      {grid.weeks[0]!.map((d) => (
        <div key={d} className="cal-dow">
          {fmtDate(d, { weekday: "short" })}
        </div>
      ))}
      {grid.weeks.flat().map((d) => {
        const items = eventsOnDay(placed, d);
        const shown = fitCount(items, d);
        return (
          <div
            key={d}
            className={`cal-cell ${d.slice(0, 7) !== month ? "other-month" : ""} ${d === today ? "today" : ""} ${d < today ? "past" : ""} ${dropClass(dnd, d)}`}
            data-drop-day={d}
            onClick={() => onDay(d)}
            role="button"
            tabIndex={0}
            aria-label={`${fmtDate(d, { weekday: "long", month: "long", day: "numeric" })}, ${items.length} events`}
          >
            <span className="cal-daynum">{Number(d.slice(8))}</span>
            {items.slice(0, shown).map((p) => (
              <Chip key={p.event.id} p={p} day={d} timeZone={timeZone} h24={h24} onEvent={onEvent} dnd={dnd} />
            ))}
            {items.length > shown &&
              (shown === 0 ? (
                <span className="mini-dots">
                  {items.slice(0, 4).map((p) => (
                    <i key={p.event.id} style={{ background: color(p) }} />
                  ))}
                </span>
              ) : (
                <span className="cal-more">+{items.length - shown} more</span>
              ))}
          </div>
        );
      })}
    </div>
  );
}

function WeekView({
  start,
  today,
  placed,
  timeZone,
  h24,
  onEvent,
  onDay,
  dnd,
}: {
  start: IsoDate;
  today: IsoDate;
  placed: PlacedEvent[];
  timeZone: string;
  h24: boolean;
  onEvent: (p: PlacedEvent) => void;
  onDay: (d: IsoDate) => void;
  dnd: DragControl;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="cal-week">
      {days.map((d) => (
        <div key={d} className={`cal-week-col ${d === today ? "today" : ""} ${d < today ? "past" : ""} ${dropClass(dnd, d)}`} data-drop-day={d}>
          <button className="cal-week-head" onClick={() => onDay(d)}>
            <span>{fmtDate(d, { weekday: "short" })}</span>
            <strong>{Number(d.slice(8))}</strong>
          </button>
          <div className="cal-week-events">
            {eventsOnDay(placed, d).map((p) => (
              <Chip key={p.event.id} p={p} day={d} timeZone={timeZone} h24={h24} onEvent={onEvent} dnd={dnd} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AgendaView({
  start,
  days,
  today,
  placed,
  timeZone,
  h24,
  byId,
  onEvent,
}: {
  start: IsoDate;
  days: number;
  today: IsoDate;
  placed: PlacedEvent[];
  timeZone: string;
  h24: boolean;
  byId: Map<string, Member>;
  onEvent: (p: PlacedEvent) => void;
}) {
  const list = Array.from({ length: days }, (_, i) => addDays(start, i))
    .map((d) => ({ d, items: eventsOnDay(placed, d) }))
    .filter((x) => x.items.length > 0);
  if (list.length === 0) return <EmptyState icon="calendar" title="Nothing scheduled" />;
  return (
    <div className="cal-agenda">
      {list.map(({ d, items }) => (
        <section key={d}>
          <h4 className={d === today ? "today" : ""}>{d === today ? "Today" : d === addDays(today, 1) ? "Tomorrow" : fmtDate(d, { weekday: "long", month: "short", day: "numeric" })}</h4>
          <DayList items={items} timeZone={timeZone} h24={h24} byId={byId} onEvent={onEvent} day={d} />
        </section>
      ))}
    </div>
  );
}

export function DayList({
  items,
  timeZone,
  h24,
  byId,
  onEvent,
  day,
}: {
  items: PlacedEvent[];
  timeZone: string;
  h24: boolean;
  byId: Map<string, Member>;
  onEvent: (p: PlacedEvent) => void;
  day?: IsoDate;
}) {
  if (items.length === 0) return <p className="muted">Nothing scheduled.</p>;
  return (
    <ol className="schedule">
      {items.map((p) => (
        <li key={p.event.id}>
          <button onClick={() => onEvent(p)} style={{ ["--ev" as string]: color(p) }}>
            <span className="sch-time">{day ? timeLabel(p, day, timeZone, h24) : p.event.allDay ? "All day" : formatTime(new Date(p.startMs), timeZone, h24)}</span>
            <span className="sch-dot" />
            <span className="sch-title">{p.event.displayTitle}</span>
            {p.event.location && <span className="sch-sub">{p.event.location}</span>}
            <People ids={p.event.people} byId={byId} />
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Stacked avatars of the people an event involves. */
function People({ ids, byId }: { ids: string[]; byId: Map<string, Member> }) {
  const people = ids.map((id) => byId.get(id)).filter((m): m is Member => Boolean(m));
  if (people.length === 0) return null;
  return (
    <span className="sch-people">
      {people.slice(0, 4).map((m) => (
        <Avatar key={m.id} member={m} size="1.75rem" />
      ))}
    </span>
  );
}

function EventDetail({
  placed,
  live,
  members,
  timeZone,
  h24,
  source,
  onClose,
  onEdit,
  onDelete,
}: {
  placed: PlacedEvent;
  live: CalendarEvent;
  members: Member[];
  timeZone: string;
  h24: boolean;
  source: string;
  onClose: () => void;
  onEdit: (e: CalendarEvent) => void;
  onDelete: (e: CalendarEvent) => void;
}) {
  const e = placed.event;
  const setPeople = useSetEventPeople();
  const [error, setError] = useState<string | null>(null);
  const when = e.allDay
    ? placed.firstDate === placed.lastDate
      ? `${fmtDate(placed.firstDate, { weekday: "long", month: "long", day: "numeric" })} · All day`
      : `${fmtDate(placed.firstDate, { month: "short", day: "numeric" })} – ${fmtDate(placed.lastDate, { month: "short", day: "numeric" })} · All day`
    : `${new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long", month: "long", day: "numeric" }).format(placed.startMs)} · ${formatTime(new Date(placed.startMs), timeZone, h24)} – ${formatTime(new Date(placed.endMs), timeZone, h24)}`;
  const toggle = (id: string) => {
    const next = live.people.includes(id) ? live.people.filter((x) => x !== id) : [...live.people, id];
    setError(null);
    setPeople.mutate({ calendarId: live.calendarId, eventKey: eventKey(live), memberIds: next }, { onError: (err) => setError((err as Error).message) });
  };
  return (
    <Sheet title={e.displayTitle} onClose={onClose} size="sm">
      <div className="event-detail" style={{ ["--ev" as string]: e.color ?? "var(--accent)" }}>
        <p className="event-when">{when}</p>
        {e.location && (
          <p>
            <Icon name="pin" size="1rem" /> {e.location}
          </p>
        )}
        {e.description && <p className="pre">{e.description}</p>}
        {members.length > 0 && (
          <div className="field">
            <span>Who’s involved?</span>
            <div className="who-picker">
              {members.map((m) => {
                const on = live.people.includes(m.id);
                return (
                  <button key={m.id} className={`who ${on ? "on" : ""}`} style={{ ["--member" as string]: m.color }} onClick={() => toggle(m.id)} aria-pressed={on}>
                    <Avatar member={m} size="2.6rem" />
                    <span>{m.displayName}</span>
                  </button>
                );
              })}
            </div>
            <p className="muted small">
              {live.recurringEventId ? "Applies to every event in this series. " : ""}
              {live.peopleSource === "title" ? "Recognized from the event’s title — tap to change." : "Colors the event and lets each person filter to their own."}
            </p>
            {error && <p className="error-text">{error}</p>}
          </div>
        )}
        {source === "sample" && <p className="muted small">This is a sample event. Connect Google Calendar to add and change your real events.</p>}
        {source === "google" && !live.canEdit && <p className="muted small">This event can’t be changed from here — it’s on a view-only calendar or was created by someone else.</p>}
        <div className="event-actions">
          {isDrivable(e.location) && <DirectionsButton target={{ name: e.displayTitle, address: e.location }} className="btn" />}
          {source === "google" && live.canEdit && (
            <>
              <button className="btn btn-primary" onClick={() => onEdit(live)}>
                <Icon name="edit" size="1.1rem" /> Edit
              </button>
              <button className="btn btn-danger-ghost" onClick={() => onDelete(live)}>
                <Icon name="trash" size="1.1rem" /> Delete
              </button>
            </>
          )}
          {e.htmlLink && (
            <OutsideLink className="btn" href={e.htmlLink} title="Open in Google Calendar">
              Open in Google Calendar
            </OutsideLink>
          )}
        </div>
      </div>
    </Sheet>
  );
}

export const calendarWidget: WidgetDefinition<CalendarConfig> = {
  type: "calendar",
  version: 1,
  name: "Family calendar",
  description: "Your shared Google Calendar: today’s schedule, week, month, or agenda.",
  icon: "calendar",
  category: "calendar",
  defaultSize: { w: 7, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: (c) => (c.view === "today" ? 440 : 560),
  defaults: { view: "today", agendaDays: 14 },
  settings: [
    {
      key: "view",
      label: "Default view",
      type: "select",
      options: VIEWS.map((v) => ({ value: v.key, label: v.label })),
    },
    { key: "agendaDays", label: "Agenda length (days)", type: "number", min: 3, max: 31 },
  ],
  provider: "calendar",
  capabilities: { requiresAuth: true },
  supportsFullscreen: true,
  Component: CalendarBody,
};
